// src/pages/EventsPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, MapPin, Plus, Users, Pencil, Trash2, Globe, Clock } from 'lucide-react';
import { Card, Badge, EmptyState, ErrorState, ConfirmModal } from '../components/ui';
import {
  getCategoryLabel,
  EVENT_CATEGORIES,
  CATEGORY_COLORS,
  CATEGORY_GRADIENTS,
} from '../constants';
import { formatDate, formatPrice } from '../utils';
import eventService from '../services/eventService';
import EventFormModal from '../modals/EventFormModal';

// ── Constantes ──

const TAB_IDS = [
  { id: 'upcoming', icon: Calendar, authOnly: false },
  { id: 'mine', icon: Users, authOnly: true },
  { id: 'past', icon: Clock, authOnly: false },
];

const PROXIMITY_RADIUS_KM = 50;

// ── Helpers ──

const haversineKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// ── Composant ──

const EventsPage = ({ user, openAuth, toast }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState('upcoming');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [nearMe, setNearMe] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  const canShowNearMe =
    user !== null && user !== undefined && user.cityLat !== null && user.cityLat !== undefined;

  const visibleTabs = useMemo(() => TAB_IDS.filter((tb) => !tb.authOnly || user), [user]);

  // Fetch selon l'onglet actif
  const fetchEvents = useCallback(async () => {
    try {
      setError(false);
      setLoading(true);
      let list = [];
      if (tab === 'upcoming') {
        list = await eventService.getUpcomingEvents(50);
      } else if (tab === 'mine' && user) {
        list = await eventService.getUserEvents(user.uid);
      } else if (tab === 'past') {
        list = await eventService.getPastEvents();
      }
      setEvents(list);
    } catch (err) {
      console.error('EventsPage fetch error:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [tab, user]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Reset filtres au changement d'onglet
  useEffect(() => {
    setCategoryFilter('');
    setNearMe(false);
  }, [tab]);

  // Filtrage côté client
  const filteredEvents = useMemo(() => {
    let list = events;

    if (categoryFilter) {
      list = list.filter((e) => e.category === categoryFilter);
    }

    if (nearMe && canShowNearMe) {
      list = list.filter((ev) => {
        if (ev.online) return true;
        const lat = ev.location?.lat;
        const lng = ev.location?.lng;
        if (lat === null || lat === undefined || lng === null || lng === undefined) return false;
        return haversineKm(user.cityLat, user.cityLng, lat, lng) <= PROXIMITY_RADIUS_KM;
      });
    }

    return list;
  }, [events, categoryFilter, nearMe, canShowNearMe, user]);

  const handleCreate = () => {
    if (!user) {
      openAuth();
      return;
    }
    setEditEvent(null);
    setFormOpen(true);
  };

  const handleEdit = (ev) => {
    setEditEvent(ev);
    setFormOpen(true);
  };

  const handleCancelEvent = async () => {
    if (!cancelTarget) return;
    const result = await eventService.cancelEvent(cancelTarget.id, user.uid);
    setCancelTarget(null);
    if (result.success) {
      toast(t('events.toast.cancelled'), 'success');
      fetchEvents();
    } else {
      toast(result.error || t('events.toast.cancelError'), 'error');
    }
  };

  // Empty state selon l'onglet
  const getEmptyState = () => {
    if (tab === 'upcoming') {
      return {
        title: t('events.empty.upcoming.title'),
        desc: user ? t('events.empty.upcoming.descAuth') : t('events.empty.upcoming.descGuest'),
      };
    }
    if (tab === 'mine') {
      return {
        title: t('events.empty.mine.title'),
        desc: t('events.empty.mine.descAuth'),
      };
    }
    return {
      title: t('events.empty.past.title'),
      desc: t('events.empty.past.desc'),
    };
  };

  const emptyState = getEmptyState();

  const tabLabels = {
    upcoming: t('events.tabs.upcoming'),
    mine: t('events.tabs.mine'),
    past: t('events.tabs.past'),
  };

  return (
    <div className="filigrane-kotaw pt-24 pb-32 lg:pb-20 container mx-auto px-4 overflow-hidden">
      <Helmet>
        <title>{t('events.meta.title')}</title>
        <meta name="description" content={t('events.meta.description')} />
        <link rel="canonical" href="https://hmonghearts.com/events" />
        <meta property="og:title" content={t('events.meta.title')} />
        <meta property="og:description" content={t('events.meta.description')} />
        <meta property="og:image" content="https://hmonghearts.com/og-default.png" />
        <meta property="og:url" content="https://hmonghearts.com/events" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        {filteredEvents.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              name: 'Événements de la communauté Hmong',
              numberOfItems: filteredEvents.length,
              itemListElement: filteredEvents.slice(0, 10).map((ev, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                url: `https://hmonghearts.com/events/${ev.id}`,
              })),
            })}
          </script>
        )}
      </Helmet>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold dark:text-gray-100">{t('events.title')}</h1>
          <p className="text-xs sm:text-sm text-gray-400 dark:text-gray-400 mt-1">
            {t('events.subtitle')}
          </p>
        </div>
        <button
          onClick={handleCreate}
          data-testid="events-btn-create"
          className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs sm:text-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white font-medium whitespace-nowrap"
        >
          <Plus size={16} />
          {t('events.create')}
        </button>
      </div>

      {/* Onglets */}
      <div className="flex gap-2 mb-5 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        {visibleTabs.map((tb) => {
          const I = tb.icon;
          return (
            <button
              key={tb.id}
              data-testid={`events-tab-${tb.id}`}
              onClick={() => setTab(tb.id)}
              className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition ${
                tab === tb.id
                  ? 'bg-rose-500 text-white'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <I size={14} className="hidden sm:block" />
              {tabLabels[tb.id]}
            </button>
          );
        })}
      </div>

      {/* Filtres : chips catégorie + toggle proximité */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex gap-2 overflow-x-auto flex-1 pb-1" style={{ scrollbarWidth: 'none' }}>
          <button
            onClick={() => setCategoryFilter('')}
            className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold transition ${
              categoryFilter === ''
                ? 'bg-rose-500 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            {t('events.filters.all')}
          </button>
          {EVENT_CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setCategoryFilter(categoryFilter === cat.value ? '' : cat.value)}
              className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold transition ${
                categoryFilter === cat.value
                  ? 'bg-rose-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {t(cat.labelKey)}
            </button>
          ))}
        </div>

        {canShowNearMe && (
          <button
            onClick={() => setNearMe((v) => !v)}
            className={`flex-shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition ${
              nearMe
                ? 'bg-rose-500 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <MapPin size={12} />
            <span className="hidden sm:inline">{t('events.filters.nearMe')}</span>
            <span className="sm:hidden">{t('events.filters.nearMeShort')}</span>
          </button>
        )}
      </div>

      {/* Contenu */}
      {error ? (
        <ErrorState onRetry={fetchEvents} />
      ) : loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="rounded-xl overflow-hidden bg-white dark:bg-gray-900 shadow-sm animate-pulse"
            >
              <div className="h-40 bg-gray-200 dark:bg-gray-700" />
              <div className="p-4 space-y-3">
                <div className="h-5 w-3/4 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded-lg" />
                <div className="space-y-1.5">
                  <div className="h-3 w-36 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                  <div className="h-3 w-28 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                  <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredEvents.length === 0 ? (
        <EmptyState
          icon={Calendar}
          title={emptyState.title}
          desc={emptyState.desc}
          ctaLabel={
            tab === 'upcoming'
              ? user
                ? t('events.cta.createEvent')
                : t('events.cta.join')
              : tab === 'mine'
                ? t('events.cta.seeEvents')
                : null
          }
          onCtaClick={
            tab === 'upcoming'
              ? user
                ? handleCreate
                : openAuth
              : tab === 'mine'
                ? () => setTab('upcoming')
                : null
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredEvents.map((ev, index) => (
            <Card
              key={ev.id}
              hover
              onClick={() => navigate(`/events/${ev.id}`)}
              className="overflow-hidden animate-fade-in-up"
              style={{ animationDelay: `${index * 60}ms` }}
              data-testid="events-card"
            >
              {/* Image / Gradient fallback */}
              <div className="relative">
                {ev.imageUrl ? (
                  <img
                    src={ev.imageUrl}
                    alt={ev.title}
                    className="w-full h-40 object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className={`h-40 bg-gradient-to-br ${CATEGORY_GRADIENTS[ev.category] || 'from-gray-400 to-gray-500'} flex items-center justify-center`}
                  >
                    <Calendar size={48} className="text-white/20" />
                  </div>
                )}
                <div className="absolute top-3 left-3">
                  <Badge c={CATEGORY_COLORS[ev.category] || 'gray'}>
                    {t(getCategoryLabel(ev.category))}
                  </Badge>
                </div>
                {ev.price > 0 && (
                  <div className="absolute top-3 right-3 bg-white/90 dark:bg-gray-900/90 text-gray-900 dark:text-gray-100 text-xs font-bold px-2 py-1 rounded-full">
                    {ev.price} €
                  </div>
                )}
              </div>

              {/* Corps */}
              <div className="p-3 sm:p-4">
                <h3 className="font-medium sm:font-bold text-sm sm:text-lg mb-1.5 dark:text-gray-100 truncate sm:line-clamp-2 sm:whitespace-normal">
                  {ev.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm mb-3 line-clamp-2">
                  {ev.description}
                </p>

                <div className="space-y-1.5 text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-3">
                  <div className="flex items-center gap-1.5">
                    <Clock size={14} className="flex-shrink-0" />
                    <span className="truncate">{formatDate(ev.date, { shortWeekday: true })}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {ev.online ? (
                      <>
                        <Globe size={14} className="flex-shrink-0" />
                        <span>{t('common.online')}</span>
                      </>
                    ) : (
                      <>
                        <MapPin size={14} className="flex-shrink-0" />
                        <span className="truncate">
                          {ev.location?.city || ev.location?.name || t('common.locationTbc')}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Users size={14} className="flex-shrink-0" />
                    <span>
                      {ev.attendeeCount || 0} {t('common.registered').toLowerCase()}
                      {(ev.attendeeCount || 0) !== 1 ? 's' : ''}
                      {ev.maxAttendees
                        ? ` ${t('events.card.places', { max: ev.maxAttendees })}`
                        : ''}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                    {formatPrice(ev.price)}
                  </span>

                  {/* Actions organisateur */}
                  {user && ev.organizerId === user.uid && (
                    <div className="flex gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(ev);
                        }}
                        className="p-1.5 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30"
                        aria-label={t('events.card.editEvent')}
                        data-testid="events-card-edit"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCancelTarget(ev);
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30"
                        aria-label={t('events.card.cancelEvent')}
                        data-testid="events-card-cancel"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modal création/édition */}
      <EventFormModal
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditEvent(null);
        }}
        user={user}
        toast={toast}
        event={editEvent}
        onSaved={fetchEvents}
      />

      {/* Modal confirmation annulation */}
      <ConfirmModal
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancelEvent}
        title={t('events.cancelConfirm.title')}
        message={t('events.cancelConfirm.message', { title: cancelTarget?.title })}
      />
    </div>
  );
};

export default EventsPage;
